import { IkeId } from '@ikegps/core-shared';
import { IkeDelayedTask } from '@ikegps/core-shared';
import { action, computed, observable, makeObservable } from 'mobx';
import { T, Translate } from '../../i18n';
import { m } from '../../mithril';
import { Services } from '../../services';
import { IMithrilComponent } from '../mithril.component';
import { NotificationService } from '../notification.toast/notification.service';
import { addStyles, DefaultTheme } from '../theme';
import { MAX_UPLOAD_FILE_SIZE_BYTE, MAX_UPLOAD_FILE_SIZE_MB } from '../../util/upload.reader';

const FileUploadCSS = {
	container: 'c-FileUpload',
	text: 'c-FileUpload__text',
	file: 'c-FileUpload__file',
	form: 'c-FileUpload__form',
	isDragging: 'c-FileUpload--is-dragging',
	isActive: 'c-FileUpload--is-active',
};

addStyles({
	[FileUploadCSS.form]: {
		width: '100%',
		height: '100%',
	},
	[FileUploadCSS.container]: {
		width: '100%',
		height: '100%',
		display: 'flex',
		'justify-content': 'center',
		'align-items': 'center',
		border: '1px solid ' + DefaultTheme.font.color.disabled,
		'&:hover': {
			border: '1px solid ' + DefaultTheme.color.accent,
			cursor: 'pointer',
		},
	},
	[FileUploadCSS.isDragging]: {
		cursor: 'inherit',
		border: 'none',
		'font-size': '200%',
		color: DefaultTheme.font.color.white,
		position: 'absolute',
		top: 0,
		left: 0,
		bottom: 0,
		right: 0,
		width: '100%',
		height: '100%',
		'background-color': DefaultTheme.font.color.supporting,
		display: 'flex',
		'justify-content': 'center',
		'align-items': 'center',
		'z-index': 99,
	},
	[FileUploadCSS.file]: {
		display: 'none',
	},
});

export const CSV_MIME_TYPE = 'text/csv';
export const EXCEL_MIME_TYPE = 'application/vnd.ms-excel';
export const OPEN_EXCEL_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
export const ZIP_MIME_TYPE = 'application/zip';
export const WINDOWS_ZIP_MIME_TYPE = 'application/x-zip-compressed';

export class FileUploadComponent extends IMithrilComponent {
	fileId: string;
	allowedTypes = [CSV_MIME_TYPE, EXCEL_MIME_TYPE, OPEN_EXCEL_MIME_TYPE, ZIP_MIME_TYPE, WINDOWS_ZIP_MIME_TYPE];
	uploadType: 'text' | 'buffer' = 'text';

	@observable isActive: boolean = false;
	@observable isDraggingFile: boolean = false;
	@observable.shallow file: FileUploadTextReader | null = null;

	@computed get isFilePresent() {
		return this.file != null;
	}

	// Slight delay to hide the drop area
	onBlurTask: IkeDelayedTask = new IkeDelayedTask(() => this.onWindowDragLeaveClear(), 25);

	constructor() {
		super();
		makeObservable(this);

		this.fileId = 'file-' + IkeId.generate();
	}

	get nodes() {
		let className = FileUploadCSS.container;
		if (this.isActive) {
			className += ' ' + FileUploadCSS.isActive;
		}
		if (this.isDraggingFile) {
			className += ' ' + FileUploadCSS.isDragging;
		}
		if (this.file) {
			return m('div', [this.file.name, m('pre', this.file.text)]);
		}
		return m('form', { className: FileUploadCSS.form }, [
			m(
				'input',
				{
					className: FileUploadCSS.file,
					type: 'file',
					id: this.fileId,
					accept: '.xlsx, .zip, application/zip, application/x-zip-compressed',
					onchange: this.onFile,
				},
				''
			),
			m(
				'div',
				{
					className,
					onclick: this.openFileDialog,
					ondragover: this.activate,
					ondragleave: this.deactivate,
					ondragend: this.deactivate,
					ondrop: this.update,
				},
				[this.isDraggingFile ? Translate(T.DROP_FILE) : Translate(T.DRAG_FILE)]
			),
		]);
	}

	@action.bound onFile(e: Event) {
		const fileInput = e.target as HTMLInputElement;
		if (fileInput == null) {
			throw new Error('Unable to find file input: "' + this.fileId + "'");
		}

		Services.Analytics.trackEvent(Services.Analytics.EventCategory.Upload, 'file:click', 'button', 1);
		this.loadFiles(fileInput.files);
	}

	@action.bound reset() {
		this.file = null;
	}

	@action.bound activate(e: DragEvent) {
		e.preventDefault();
		this.isActive = true;
	}

	@action.bound deactivate() {
		this.isActive = false;
	}

	@action.bound update(e: DragEvent) {
		e.preventDefault();

		if (e.dataTransfer == null) {
			return;
		}
		this.deactivate();
		this.isDraggingFile = false;

		const files = e.dataTransfer.files;

		Services.Analytics.trackEvent(Services.Analytics.EventCategory.Upload, 'file:drop', 'drop', 1);
		this.loadFiles(files);
	}

	loadFiles(files: FileList) {
		this.file = null;

		if (files == null) {
			return;
		}

		if (files.length > 1) {
			Services.Analytics.trackEvent(Services.Analytics.EventCategory.Upload, 'file:count', String(files.length), 1);
			NotificationService.error({ key: T.FILE_NOT_SUPPORTED });
			return;
		}

		for (let i = 0; i < files.length; i++) {
			const file = files[i];
			if (!file) {
				return;
			}
			if (MAX_UPLOAD_FILE_SIZE_BYTE < file.size) {
				Services.Analytics.trackEvent(Services.Analytics.EventCategory.Upload, 'file:large', 'large', file.size);
				NotificationService.error({ key: T.FILE_TOO_LARGE, attrs: { limit: `${MAX_UPLOAD_FILE_SIZE_MB} MB` } });
				continue;
			}

			const fileType = file.type.toLowerCase();
			if (fileType !== '' && this.allowedTypes.indexOf(fileType) === -1) {
				Services.Analytics.trackEvent(Services.Analytics.EventCategory.Upload, 'file:type', fileType, 1);
				NotificationService.error({ key: T.FILE_NOT_SUPPORTED });
				continue;
			}

			this.file = new FileUploadTextReader(file, this.uploadType);
		}
	}

	@action.bound openFileDialog(e: MouseEvent) {
		e.preventDefault();

		const fileInput = document.getElementById(this.fileId);
		if (fileInput == null) {
			throw new Error('Unable to find file input: "' + this.fileId + "'");
		}

		var evt = document.createEvent('MouseEvents');
		evt.initEvent('click', true, false);
		fileInput.dispatchEvent(evt);
	}

	@action.bound onWindowDragOver(e: DragEvent) {
		this.onBlurTask.cancel();
		if (e.dataTransfer && e.dataTransfer.types && e.dataTransfer.types.indexOf('Files') > -1) {
			this.isDraggingFile = true;
		}
	}

	@action.bound onWindowDragLeave(e: Event) {
		if (this.onBlurTask.isActive) {
			return;
		}
		this.onBlurTask.delay();
	}

	@action.bound onWindowDragLeaveClear() {
		this.isDraggingFile = false;
	}

	override onCreate() {
		Services.Window.document.addEventListener('blur', this.onWindowDragLeave);
		Services.Window.document.addEventListener('dragleave', this.onWindowDragLeave);
		Services.Window.document.addEventListener('dragover', this.onWindowDragOver);
	}

	override onRemove() {
		Services.Window.document.removeEventListener('blur', this.onWindowDragLeave);
		Services.Window.document.removeEventListener('dragleave', this.onWindowDragLeave);
		Services.Window.document.removeEventListener('dragover', this.onWindowDragOver);
	}
}

class FileUploadTextReader {
	type: string;
	name: string;
	size: number;
	file: File;

	@observable text: string | null = null;
	@observable isFailed: boolean = false;
	@observable isLoading: boolean = true;
	fileReader: FileReader = new FileReader();

	constructor(file: File, type: 'text' | 'buffer') {
		makeObservable(this);

		this.file = file;
		this.name = file.name;
		this.type = file.type;
		this.size = file.size;

		this.fileReader.onerror = this.onFileError;
		this.fileReader.onload = this.onFileLoad;

		if (type === 'text') {
			this.fileReader.readAsText(file);
		} else {
			this.fileReader.readAsArrayBuffer(file);
		}
	}

	@action.bound onFileError() {
		this.fileReader.abort();
		this.isFailed = true;
		this.isLoading = false;
	}

	@action.bound onFileLoad() {
		this.isFailed = false;
		this.isLoading = false;
		this.text = this.fileReader.result as string;
	}
}
